import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Flex, Select, Text, useToast} from '@chakra-ui/react';
import axios from 'axios';

type Props = {role: 'customers' | 'providers'; filters?:Record<string,string>; disabled?:boolean; deleted?:boolean};
export default function UserExport(props: Props) {
  // Changing directory must reset the selection and cancel its previous export.
  return <UserExportForRole key={JSON.stringify([props.role,props.filters,props.deleted])} {...props} />;
}

function UserExportForRole({role,filters,disabled,deleted}: Props) {
  const [scope, setScope] = useState(role as string);
  const [busy, setBusy] = useState<'csv' | 'xlsx' | null>(null);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => {pending.current?.abort();}, []);
  const toast = useToast();
  const group = scope === 'all' ? 'all users' : scope === 'customers' ? 'customers' : 'service providers';
  const providerOnly = !!(filters?.approvalGroups || filters?.profileStatuses || filters?.identityStatuses);
  async function download(format: 'csv' | 'xlsx') {
    if (pending.current || disabled || deleted) return;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(format);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/admin/users/export`, {
        params: {...filters, ...(filters ? {applyFilters:'true'} : {}), role: scope, format}, responseType: 'blob', timeout: 120000,
        withCredentials: true,
        signal: controller.signal,
      });
      // Some adapters may resolve even after cancellation. Never download a
      // previous directory's response on the page the admin navigated to.
      if (controller.signal.aborted || pending.current !== controller) return;
      const expected = format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (!String(response.headers['content-type']).includes(expected)) throw new Error('The server did not return an export. Please try again.');
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Pureworker_${scope === 'all' ? 'All_Users' : scope === 'customers' ? 'Customers' : 'Providers'}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast({title: `Export downloaded: ${group}`, status: 'success', duration: 4000, isClosable: true});
    } catch (error) {
      if (controller.signal.aborted) return;
      toast({title: 'Export failed', description: 'No complete export was downloaded. Please try again; if your session expired, sign in again.', status: 'error', duration: 8000, isClosable: true});
    } finally {
      if (pending.current === controller) {
        pending.current = null;
        if (!controller.signal.aborted) setBusy(null);
      }
    }
  }
  return <Box mb={4}>
    <Flex gap={2} wrap="wrap" align="center">
      <Select aria-label="Export accounts" value={scope} onChange={e => setScope(e.target.value)} isDisabled={!!busy} width={{base: '100%', md: '280px'}}>
        <option value={role}>All {role === 'customers' ? 'customers' : 'service providers'}</option>
        <option value="all" disabled={providerOnly}>Customers and service providers</option>
      </Select>
      <Button colorScheme="purple" onClick={() => download('csv')} isDisabled={!!busy || disabled || deleted} isLoading={busy === 'csv'} loadingText={`Exporting ${group}`}>Export {group} CSV (SMS)</Button>
      <Button colorScheme="purple" variant="outline" onClick={() => download('xlsx')} isDisabled={!!busy || disabled || deleted} isLoading={busy === 'xlsx'} loadingText={`Exporting ${group}`}>Export {group} Excel</Button>
    </Flex>
    <Text fontSize="sm" mt={2}>{filters ? 'Includes accounts in the selected group matching all search and filter selections, across all pages. Changing filters cancels an export in progress.' : 'Includes every current account in the selected group, across all pages. Search filters do not apply.'} Deleted accounts are excluded.</Text>
    {deleted && <Text fontSize="sm" mt={1}>Turn off Show deleted accounts to export current accounts.</Text>}
    <Text fontSize="sm">Nigerian numbers use 080…, 090… format. Use Excel export to keep leading zeros when opening a spreadsheet, or upload the CSV directly to your SMS service. Check “Phone Status” for numbers needing review.</Text>
  </Box>;
}
